import { useState } from 'react'
import type { EventPageExtra, EventPageFeaturedEvent, EventPageRaceTimeEvent, EventPageSponsor, EventPageViewer, SignupState } from '../data/types'
import { truthy } from '../data/types'
import { formatDate, formatMoney } from '../data/format'
import { sanitizeRichText } from '../data/richText'
import { postAddEventExtra, postRemoveEventExtra } from '../data/eventPageApi'
import styles from './Lists.module.css'

/** logoUrl is a static /shared/images/manLogo/{coCode}_logo.gif file (same convention as
 *  api.cfc's own company.logoPath), unconditionally built server-side with no existence check -
 *  a company without a real logo file still gets a URL that 404s. Falls back to the tile's plain
 *  background (no broken-image icon) rather than a broken-image icon. No caption underneath (an
 *  explicit ask), so `name` carries over as `alt`/`title` instead - still available on hover/to
 *  a screen reader, just not shown as visible text. */
function SponsorLogo({ src, name }: { src: string; name: string }) {
  const [broken, setBroken] = useState(false)
  return (
    <div className={styles.sponsorLogoBox} title={name}>
      {!broken ? <img src={src} alt={name} className={styles.sponsorLogo} onError={() => setBroken(true)} /> : null}
    </div>
  )
}

export function Sponsors({ sponsors, id }: { sponsors: EventPageSponsor[]; id?: string }) {
  if (!sponsors.length) return null
  return (
    <div id={id} className={styles.section}>
      <h2 className={styles.heading}>Sponsors</h2>
      <div className={styles.sponsorGrid}>
        {sponsors.map((s) => (
          <SponsorLogo key={s.coCode} src={s.logoUrl} name={s.coName + (s.levelName ? ` · ${s.levelName}` : '')} />
        ))}
      </div>
    </div>
  )
}

// Shared by RaceTimeCard and FeaturedEventsCard below - both are sitewide paid-promotion cards
// (not data about the event this page is showing) with the same "[date range]" + name + venue
// shape, just styled for a different surface (dark vs. light) and with/without a flyer.
function eventDateRange(startDT: string, endDT: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: '2-digit' }
  return [formatDate(startDT, opts), formatDate(endDT, opts)].filter(Boolean).join(' – ')
}
function eventVenueLine(venueName: string, city: string, state: string): string {
  return [venueName, [city, state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')
}

/** Sitewide paid-partner promo, same "RaceTime Entertainment" card as
 *  _right_side_bar_default.cfm's legacy sidebar widget (events flagged
 *  eventprofile.racetimeevent=1) - not data about the event this page is showing. Matches
 *  Desktop.dc.html/Main.dc.html's dark aside card, minus its "Tier 1 partner" badge (an explicit
 *  ask - no partner-tier concept exists in this data yet). */
export function RaceTimeCard({ events }: { events: EventPageRaceTimeEvent[] }) {
  if (!events.length) return null
  return (
    <div className={styles.raceTimeCard}>
      <h2 className={styles.raceTimeTitle}>RaceTime Entertainment</h2>
      {events.map((e) => {
        const dateRange = eventDateRange(e.startDT, e.endDT)
        const venueLine = eventVenueLine(e.venueName, e.city, e.state)
        return (
          <a key={e.id} href={`/events/displayEvent.cfm?ID=${e.id}`} className={styles.raceTimeItem}>
            {e.flyerUrl ? (
              <img src={e.flyerUrl} alt="" className={styles.raceTimeThumb} />
            ) : (
              <div className={styles.raceTimeThumbPlaceholder}>Flyer</div>
            )}
            <div className={styles.raceTimeInfo}>
              {dateRange ? <span className={styles.raceTimeDate}>{dateRange}</span> : null}
              <span className={styles.raceTimeName}>{e.name}</span>
              {venueLine ? <span className={styles.raceTimeVenue}>{venueLine}</span> : null}
            </div>
          </a>
        )
      })}
    </div>
  )
}

/** Sitewide paid-promotion card, same "Featured Events" widget as
 *  _right_side_bar_default.cfm's legacy sidebar (events.cfc::getFeaturedEvents, a paid
 *  userorders upgrade) - not data about the event this page is showing. Matches
 *  Desktop.dc.html/Main.dc.html's orange-tinted-border aside card, minus its flyer thumbnail (an
 *  explicit ask). */
export function FeaturedEventsCard({ events }: { events: EventPageFeaturedEvent[] }) {
  if (!events.length) return null
  return (
    <div className={styles.featuredCard}>
      <h2 className={styles.featuredTitle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
        </svg>
        Featured events
      </h2>
      {events.map((e) => {
        const dateRange = eventDateRange(e.startDT, e.endDT)
        const venueLine = eventVenueLine(e.venueName, e.city, e.state)
        return (
          <a key={e.id} href={`/events/displayEvent.cfm?ID=${e.id}`} className={styles.featuredItem}>
            {dateRange ? <span className={styles.featuredDate}>{dateRange}</span> : null}
            <span className={styles.featuredName}>{e.name}</span>
            {venueLine ? <span className={styles.featuredVenue}>{venueLine}</span> : null}
          </a>
        )
      })}
    </div>
  )
}

/** The interactive add-controls for one extra, logged-in racer only - flat is a single immediate
 *  "Add", number is a quantity select + "Add", rvhookup is days + hookup-type selects + "Add".
 *  Mirrors racers/profile/_signupClassList.cfm's three extra types, same immediate-save pattern
 *  as VehicleSignupModal (see the "Real class signup" plan). */
function ExtraControls({
  eventId,
  currency,
  price,
  extraId,
  signupExtra,
  onSaved,
}: {
  eventId: number
  currency: string
  price: number | string
  extraId: number
  signupExtra: SignupState['extras'][number]
  onSaved: (next: SignupState) => void
}) {
  const [quantity, setQuantity] = useState<number | ''>('')
  const [optionId, setOptionId] = useState<number | ''>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [removing, setRemoving] = useState(false)

  if (truthy(signupExtra.added)) {
    const paid = truthy(signupExtra.paid)

    // Inline confirm instead of window.confirm() - same reasoning as ClassList's SignupRowDetail.
    if (confirming) {
      return (
        <div className={styles.extraControls}>
          <span className={styles.confirmText}>Remove this add-on?</span>
          <button
            type="button"
            className={styles.confirmYes}
            disabled={removing}
            onClick={() => {
              setRemoving(true)
              setError(null)
              postRemoveEventExtra(eventId, extraId)
                .then((next) => {
                  // Reset here too, not just in .catch() - this component instance isn't
                  // remounted when signupExtra.added flips back to false (same list position,
                  // same extraId), so a stale `removing`/`confirming` left over from a *previous*
                  // successful remove would otherwise resurface - wrongly - if this extra gets
                  // added again later and its "added" branch (which checks `confirming` first)
                  // renders again. Same reasoning as the `saving` reset in `save()` below.
                  setRemoving(false)
                  setConfirming(false)
                  onSaved(next)
                })
                .catch((err: unknown) => {
                  setError(err instanceof Error ? err.message : String(err))
                  setRemoving(false)
                  setConfirming(false)
                })
            }}
          >
            {removing ? 'Removing…' : 'Yes'}
          </button>
          <button type="button" className={styles.confirmNo} disabled={removing} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </div>
      )
    }

    return (
      <div className={styles.extraStatus}>
        {paid ? (
          <span className={styles.extraPaidBadge}>PAID</span>
        ) : (
          <>
            {formatMoney(signupExtra.amount, currency)}
            <button type="button" className={styles.removeBtn} onClick={() => setConfirming(true)} aria-label="Remove this add-on">
              ×
            </button>
          </>
        )}
        {error ? <div className={styles.extraError}>{error}</div> : null}
      </div>
    )
  }

  const save = (qty: number, opt: number) => {
    setSaving(true)
    setError(null)
    postAddEventExtra(eventId, extraId, qty, opt)
      .then((next) => {
        // Reset here too, not just in .catch() - this component instance isn't remounted when
        // signupExtra.added flips to true (same list position, same extraId), so a stale
        // `saving=true` left behind here would otherwise sit dormant (irrelevant while the
        // "added" branch above is showing) and then wrongly resurface as a permanently-disabled
        // "Adding…" button if this extra is later removed and falls back to this branch.
        setSaving(false)
        onSaved(next)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err))
        setSaving(false)
      })
  }

  if (signupExtra.type === 'flat') {
    return (
      <div className={styles.extraControls}>
        <button type="button" className={styles.extraAdd} disabled={saving} onClick={() => save(0, 0)}>
          {saving ? 'Adding…' : `Add - ${formatMoney(price, currency)}`}
        </button>
        {error ? <div className={styles.extraError}>{error}</div> : null}
      </div>
    )
  }

  if (signupExtra.type === 'number') {
    return (
      <div className={styles.extraControls}>
        <select value={quantity} onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : '')} disabled={saving}>
          <option value="">Qty</option>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <button type="button" className={styles.extraAdd} disabled={!quantity || saving} onClick={() => quantity && save(quantity, 0)}>
          {saving ? 'Adding…' : 'Add'}
        </button>
        {error ? <div className={styles.extraError}>{error}</div> : null}
      </div>
    )
  }

  if (signupExtra.type === 'rvhookup') {
    const soldOut = signupExtra.remaining !== undefined && signupExtra.remaining <= 0
    if (soldOut) return <span className={styles.extraSoldOut}>SOLD OUT</span>
    return (
      <div className={styles.extraControls}>
        <select value={quantity} onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : '')} disabled={saving}>
          <option value="">Days</option>
          {Array.from({ length: Number(signupExtra.maxDays) || 0 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <select value={optionId} onChange={(e) => setOptionId(e.target.value ? Number(e.target.value) : '')} disabled={saving}>
          <option value="">Hookup type</option>
          {(signupExtra.options || []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.label} - {formatMoney(o.price, currency)}/day
            </option>
          ))}
        </select>
        <button type="button" className={styles.extraAdd} disabled={!quantity || !optionId || saving} onClick={() => quantity && optionId && save(quantity, optionId)}>
          {saving ? 'Adding…' : 'Add'}
        </button>
        {error ? <div className={styles.extraError}>{error}</div> : null}
      </div>
    )
  }

  return null
}

export function Extras({
  extras,
  dollarType,
  viewer,
  signup,
  onSignupChange,
  eventId,
}: {
  extras: EventPageExtra[]
  dollarType: string
  viewer: EventPageViewer
  signup: SignupState | null
  onSignupChange: (next: SignupState) => void
  eventId: number
}) {
  if (!extras.length) return null
  const interactive = viewer.type === 'racer' && truthy(viewer.loggedIn) && signup

  return (
    <div className={styles.section}>
      <h2 className={styles.heading}>Add-ons</h2>
      {extras.map((e) => {
        const signupExtra = interactive && signup ? signup.extras.find((x) => x.id === e.id) : undefined
        const added = Boolean(signupExtra && truthy(signupExtra.added))
        return (
          <div key={e.id} className={`${styles.extraCard} ${added ? styles.extraCardEntered : ''}`}>
            <div>
              <div className={styles.extraName}>{e.name}</div>
              {/* Rendered like the legacy page's unescaped `#extraDesc#` - same trust boundary as
                  event.description, see App.tsx. */}
              {/* eslint-disable-next-line react/no-danger */}
              {e.description ? <div className={styles.extraDesc} dangerouslySetInnerHTML={{ __html: sanitizeRichText(e.description) }} /> : null}
            </div>
            {signupExtra ? (
              <ExtraControls eventId={eventId} currency={dollarType || 'USD'} price={e.price} extraId={e.id} signupExtra={signupExtra} onSaved={onSignupChange} />
            ) : (
              <div className={styles.extraPrice}>{formatMoney(e.price, dollarType || 'USD')}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}
